/**
 * SchemesAdminPage.jsx
 * ─────────────────────
 * Three sections:
 *
 * 1. UPLOAD — upload Excel workbook for any crop
 *
 * 2. MANAGE — crop tabs (Almond | Olive | Grapevine …)
 *    Click a tab → see all schemes for that crop grouped by complexity
 *    Score editor inline per scheme row
 *
 * 3. ATTACH DOCUMENTS — automatically filtered to the active crop tab
 *    Scheme select shows only schemes from the active crop
 *    Then option select loads options for the chosen scheme
 */

import { useEffect, useRef, useState } from "react";
import { uploadExcel, listSchemes, updateScore, deleteScheme, attachDocument } from "../../services/api";

const BASE     = "/api";
const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");
const CMPLX_ORDER = ["Low", "Medium", "High"];

// ── Section wrapper ────────────────────────────────────────────────────────────

function Section({ title, sub, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 12,
      padding: "1.25rem 1.5rem", marginBottom: "1.5rem" }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: sub ? 4 : "1rem", color: "#212529" }}>
        {title}
      </h3>
      {sub && <p style={{ fontSize: 13, color: "#6c757d", marginBottom: "1rem" }}>{sub}</p>}
      {children}
    </div>
  );
}

// ── Score cell ─────────────────────────────────────────────────────────────────

// ── Confidence slider with visual bar ────────────────────────────────────────

function ConfBar({ value, onChange, label }) {
  const pct   = Math.round((value || 0) * 100);
  const color = pct >= 67 ? "#4B7518" : pct >= 34 ? "#E8A020" : "#D63B2F";
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#5C4B00",
        display: "flex", justifyContent: "space-between" }}>
        <span>{label}</span>
        <span style={{ color, fontWeight: 700 }}>{pct}%</span>
      </label>
      <div style={{ position: "relative", height: 8, background: "#e0e8d0",
        borderRadius: 4, marginTop: 4, cursor: "pointer" }}>
        {/* Coloured fill */}
        <div style={{ position: "absolute", left: 0, top: 0,
          width: `${pct}%`, height: "100%", borderRadius: 4, background: color,
          transition: "width .1s, background .15s" }}/>
        <input type="range" min="0" max="1" step="0.01"
          value={value ?? 0}
          onChange={e => onChange(parseFloat(e.target.value))}
          style={{ position: "absolute", inset: 0, opacity: 0,
            cursor: "pointer", width: "100%", margin: 0 }}/>
      </div>
      {/* Zone labels */}
      <div style={{ display: "flex", justifyContent: "space-between",
        fontSize: 9, color: "#aaa", marginTop: 2 }}>
        <span style={{ color: "#D63B2F" }}>Low</span>
        <span style={{ color: "#E8A020" }}>Medium</span>
        <span style={{ color: "#4B7518" }}>High</span>
      </div>
    </div>
  );
}

// ── EvaluationCell — replaces old ScoreCell ───────────────────────────────────

function EvaluationCell({ scheme, onSaved }) {
  const [editing,   setEditing]   = useState(false);
  const [ipm,       setIpm]       = useState(scheme.int_perf_mean  ?? "");
  const [ipc,       setIpc]       = useState(scheme.int_perf_conf  ?? 0.5);
  const [arm,       setArm]       = useState(scheme.adp_ready_mean ?? "");
  const [arc,       setArc]       = useState(scheme.adp_ready_conf ?? 0.5);
  const [comment,   setComment]   = useState(scheme.expert_comment ?? "");
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState("");

  function hasData() {
    return scheme.int_perf_mean != null || scheme.adp_ready_mean != null;
  }

  function scoreColor(v) {
    if (v == null) return "#adb5bd";
    return v >= 7 ? "#2d6b0a" : v >= 5 ? "#7a4600" : "#8a1212";
  }

  async function save() {
    const ipmF = ipm !== "" ? parseFloat(ipm) : null;
    const armF = arm !== "" ? parseFloat(arm) : null;
    if (ipmF != null && (ipmF < 0 || ipmF > 10)) { setMsg("Mean score must be 0–10."); return; }
    if (armF != null && (armF < 0 || armF > 10)) { setMsg("Mean score must be 0–10."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/schemes/${scheme.id}/score`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          int_perf_mean:  ipmF,
          int_perf_conf:  ipc,
          adp_ready_mean: armF,
          adp_ready_conf: arc,
          expert_comment: comment.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? "Save failed");
      setMsg("Saved ✓");
      onSaved();
      setTimeout(() => { setEditing(false); setMsg(""); }, 900);
    } catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  }

  function MiniScore({ val }) {
    if (val == null) return <span style={{ fontSize: 12, color: "#adb5bd" }}>—</span>;
    const c = scoreColor(val);
    return (
      <span style={{ fontSize: 13, fontWeight: 800, color: c,
        fontFamily: "'Nunito',sans-serif" }}>
        {val.toFixed(1).replace(".", ",")}
      </span>
    );
  }

  // Display (read) view
  if (!editing) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, color: "#775F46", fontWeight: 600,
          textTransform: "uppercase", minWidth: 110 }}>Int. Performance</span>
        <MiniScore val={scheme.int_perf_mean}/>
        {scheme.int_perf_conf != null && (
          <span style={{ fontSize: 10, color: "#888" }}>
            ({Math.round(scheme.int_perf_conf * 100)}% conf.)
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 10, color: "#775F46", fontWeight: 600,
          textTransform: "uppercase", minWidth: 110 }}>Adoption Ready</span>
        <MiniScore val={scheme.adp_ready_mean}/>
        {scheme.adp_ready_conf != null && (
          <span style={{ fontSize: 10, color: "#888" }}>
            ({Math.round(scheme.adp_ready_conf * 100)}% conf.)
          </span>
        )}
      </div>
      <button onClick={() => setEditing(true)}
        style={{ marginTop: 3, fontSize: 11, padding: "2px 10px",
          borderRadius: 5, border: "1px solid #d4dbc8", background: "none",
          cursor: "pointer", color: "#5C4B00", width: "fit-content",
          fontFamily: "'Nunito',sans-serif" }}>
        {hasData() ? "Edit evaluation" : "Add evaluation"}
      </button>
    </div>
  );

  // Edit (form) view
  return (
    <div style={{ minWidth: 310, background: "#fafcf6", border: "1.5px solid #D4D980",
      borderRadius: 10, padding: "12px 14px" }}>
      {msg && (
        <div style={{ fontSize: 11, marginBottom: 8, padding: "4px 8px", borderRadius: 5,
          background: msg.includes("✓") ? "#EAF3DE" : "#FCEBEB",
          color: msg.includes("✓") ? "#1e4a00" : "#A32D2D" }}>{msg}</div>
      )}

      {/* ── Integrated Performance ── */}
      <div style={{ marginBottom: 14, paddingBottom: 12,
        borderBottom: "1px solid rgba(212,217,128,.4)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#4B7518",
          textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>
          Integrated Performance
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#5C4B00" }}>
              Mean score (0–10)
            </label>
            <input type="number" min="0" max="10" step="0.1"
              className="form-control form-control-sm mt-1"
              style={{ fontSize: 12 }}
              placeholder="e.g. 7.4"
              value={ipm} onChange={e => setIpm(e.target.value)}/>
          </div>
          <div />
        </div>
        <div style={{ marginTop: 8 }}>
          <ConfBar value={ipc} onChange={setIpc} label="Confidence level"/>
        </div>
      </div>

      {/* ── Adoption Readiness ── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#4B7518",
          textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>
          Adoption Readiness
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#5C4B00" }}>
              Mean score (0–10)
            </label>
            <input type="number" min="0" max="10" step="0.1"
              className="form-control form-control-sm mt-1"
              style={{ fontSize: 12 }}
              placeholder="e.g. 8.7"
              value={arm} onChange={e => setArm(e.target.value)}/>
          </div>
          <div />
        </div>
        <div style={{ marginTop: 8 }}>
          <ConfBar value={arc} onChange={setArc} label="Confidence level"/>
        </div>
      </div>

      {/* ── Expert comment ── */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "#5C4B00" }}>
          Expert comment <span style={{ fontWeight: 400, color: "#adb5bd" }}>(optional)</span>
        </label>
        <input type="text" className="form-control form-control-sm mt-1"
          style={{ fontSize: 11 }}
          placeholder="Free text remark…"
          value={comment} onChange={e => setComment(e.target.value)}/>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm btn-primary" style={{ fontSize: 12 }}
          onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save evaluation"}
        </button>
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 12 }}
          onClick={() => { setEditing(false); setMsg(""); }}>Cancel</button>
      </div>
    </div>
  );
}


function AttachDocForm({ schemesForCrop, activeCrop, onAttached }) {
  const [selScheme,    setSelScheme]    = useState("");
  const [options,      setOptions]      = useState([]);
  // "measure|||option_text" so we carry both in one state value
  const [selOptionKey, setSelOptionKey] = useState("");
  const [title,        setTitle]        = useState("");
  const [file,         setFile]         = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [msg,          setMsg]          = useState({ text: "", ok: true });
  const fileRef = useRef();

  useEffect(() => {
    setSelScheme(""); setSelOptionKey(""); setOptions([]);
    setFile(null); setMsg({ text: "", ok: true });
  }, [activeCrop]);

  async function loadOptions(schemeId) {
    setSelOptionKey(""); setOptions([]);
    if (!schemeId) return;
    try {
      const res  = await fetch(`${BASE}/schemes/${schemeId}/options`,
        { headers: { "Authorization": `Bearer ${getToken()}` } });
      setOptions(await res.json());
    } catch { setOptions([]); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!file || !selScheme) {
      setMsg({ text: "Select a scheme and choose a file.", ok: false });
      return;
    }
    setUploading(true); setMsg({ text: "", ok: true });

    const [optMeasure, ...rest] = selOptionKey ? selOptionKey.split("|||") : ["", ""];
    const optText = rest.join("|||");

    const fd = new FormData();
    fd.append("file",           file);
    fd.append("scheme_id",      selScheme);
    fd.append("option_text",    optText    || "");
    fd.append("option_measure", optMeasure || "");
    fd.append("title",          title);

    try {
      const res  = await fetch(`${BASE}/schemes/documents/upload`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ?? "Upload failed");
      setMsg({ text: `✓ "${file.name}" attached.`, ok: true });
      setFile(null); setTitle(""); setSelOptionKey("");
      fileRef.current.value = "";
      onAttached();
    } catch (err) { setMsg({ text: err.message, ok: false }); }
    finally { setUploading(false); }
  }

  const grouped = {};
  schemesForCrop.forEach((s) => {
    const c = s.complexity ?? "Other";
    if (!grouped[c]) grouped[c] = [];
    grouped[c].push(s);
  });

  const optMeasureSelected = selOptionKey ? selOptionKey.split("|||")[0] : "";
  const optTextSelected    = selOptionKey ? selOptionKey.split("|||").slice(1).join("|||") : "";

  return (
    <form onSubmit={submit}>
      {msg.text && (
        <div className={`alert py-2 mb-3 ${msg.ok ? "alert-success" : "alert-danger"}`}
             style={{ fontSize: 13 }}>{msg.text}</div>
      )}
      {schemesForCrop.length === 0 ? (
        <div style={{ fontSize: 13, color: "#adb5bd" }}>
          No schemes for <strong>{activeCrop || "this crop"}</strong>. Upload an Excel first.
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500 }}>Scheme *</label>
              <select className="form-select form-select-sm mt-1" value={selScheme} required
                onChange={(e) => { setSelScheme(e.target.value); loadOptions(e.target.value); }}>
                <option value="">Select scheme…</option>
                {CMPLX_ORDER.filter((c) => grouped[c]).map((c) => (
                  <optgroup key={c} label={`${c} complexity`}>
                    {grouped[c].map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500 }}>
                Option{" "}
                <span style={{ fontWeight: 400, color: "#adb5bd" }}>
                  (leave blank for a general scheme paper)
                </span>
              </label>
              <select className="form-select form-select-sm mt-1"
                value={selOptionKey}
                onChange={(e) => setSelOptionKey(e.target.value)}
                disabled={!selScheme}>
                <option value="">General scheme paper</option>
                {["prevention","monitoring","intervention"].map((m) => {
                  const opts = options.filter((o) => o.measure === m);
                  return opts.length === 0 ? null : (
                    <optgroup key={m} label={m.charAt(0).toUpperCase()+m.slice(1)}>
                      {opts.map((o) => (
                        <option key={o.id} value={`${m}|||${o.option_text}`}>
                          {o.option_text}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
              {selOptionKey && (
                <div style={{ fontSize: 11, color: "#185FA5", marginTop: 4 }}>
                  ℹ Will appear under <strong>"{optTextSelected}"</strong> in ALL
                  schemes that contain this option.
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500 }}>
                Title <span style={{ fontWeight: 400, color: "#adb5bd" }}>(optional)</span>
              </label>
              <input type="text" className="form-control form-control-sm mt-1"
                placeholder="e.g. IPM Guidelines 2023" value={title}
                onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500 }}>File * (PDF, Word, TXT)</label>
              <div className="d-flex gap-2 mt-1 align-items-center">
                <input type="file" accept=".pdf,.docx,.doc,.txt" ref={fileRef}
                  onChange={(e) => setFile(e.target.files[0])} style={{ display: "none" }} />
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={() => fileRef.current.click()}>
                  {file ? "✓ " + file.name : "Choose file…"}
                </button>
                {file && (
                  <button type="button" className="btn btn-outline-secondary btn-sm"
                    onClick={() => { setFile(null); fileRef.current.value = ""; }}>✕</button>
                )}
              </div>
            </div>
          </div>
          <button className="btn btn-primary btn-sm" type="submit"
            disabled={uploading || !file || !selScheme}>
            {uploading ? "Uploading…" : "Attach document"}
          </button>
        </>
      )}
    </form>
  );
}


// ── Main page ──────────────────────────────────────────────────────────────────

export default function SchemesAdminPage() {
  const [schemes,    setSchemes]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [uploading,  setUploading]  = useState(false);
  const [uploadMsg,  setUploadMsg]  = useState({ text: "", ok: true });
  const [activeCrop, setActiveCrop] = useState(null);
  const [activeTab,  setActiveTab]  = useState("manage");
  const excelRef = useRef();

  useEffect(() => { setActiveTab("manage"); }, [activeCrop]);

  useEffect(() => { load(); }, []);

  async function load(switchToCrop = null) {
    setLoading(true); setError("");
    try {
      const data = await listSchemes();
      setSchemes(data);
      const crops = [...new Set(data.map((s) => s.crop_id))].sort();
      if (switchToCrop && crops.includes(switchToCrop)) {
        // After an upload — switch to the newly uploaded crop tab
        setActiveCrop(switchToCrop);
      } else {
        // On refresh — keep current tab if still valid, else fall back to first
        setActiveCrop((prev) => {
          if (prev && crops.includes(prev)) return prev;
          return crops[0] ?? null;
        });
      }
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }

  async function handleExcel(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg({ text: `Importing "${file.name}"…`, ok: true });
    try {
      const data = await uploadExcel(file);
      setUploadMsg({
        text: `✓ ${data.message}${data.warnings?.length ? ` — ${data.warnings.length} warning(s)` : ""}`,
        ok:   true,
      });
      // Derive crop_id from crop name e.g. "Almond" -> "almond"
      const newCropId = data.crop?.toLowerCase().replace(/\s+/g, "_");
      await load(newCropId || null);
    } catch (err) {
      setUploadMsg({ text: err.message, ok: false });
    } finally {
      setUploading(false);
      excelRef.current.value = "";
    }
  }

  async function handleDelete(schemeId) {
    if (!window.confirm(`Delete scheme ${schemeId}? This also removes its options.`)) return;
    try { await deleteScheme(schemeId); await load(); }
    catch (e) { setError(e.message); }
  }

  // ── Derived data ─────────────────────────────────────────────────────────────
  const cropIds = [...new Set(schemes.map((s) => s.crop_id))].sort();

  const schemesForCrop = schemes.filter((s) => s.crop_id === activeCrop);

  const byComplexity = {};
  schemesForCrop.forEach((s) => {
    const c = s.complexity;
    if (!byComplexity[c]) byComplexity[c] = [];
    byComplexity[c].push(s);
  });

  const cropDisplayName = (id) => id ? id.charAt(0).toUpperCase() + id.slice(1).replace("_", " ") : "";

  return (
    <div className="p-4">
      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: "1.5rem" }}>IPM Schemes</h2>

      {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}

      {/* ── Section 1: Upload Excel ── */}
      <Section title="1. Upload Excel workbook"
        sub="Upload an IPM Excel file for any crop. All complexity sheets are parsed automatically.">
        {uploadMsg.text && (
          <div className={`alert py-2 mb-3 ${uploadMsg.ok ? "alert-success" : "alert-danger"}`}
               style={{ fontSize: 13 }}>{uploadMsg.text}</div>
        )}
        <input type="file" accept=".xlsx,.xls" ref={excelRef}
          onChange={handleExcel} style={{ display: "none" }} />
        <button className="btn btn-primary btn-sm"
          onClick={() => excelRef.current.click()} disabled={uploading}>
          {uploading ? "Processing…" : "Upload Excel file"}
        </button>
        <span style={{ fontSize: 12, color: "#6c757d", marginLeft: 12 }}>
          Supported: .xlsx / .xls · max 20 MB
        </span>
      </Section>

      {/* ── Section 2: Manage schemes ── */}
      <Section title="2. Manage schemes"
        sub="Select a crop, then switch between tabs to score schemes or attach reference documents.">
        {loading ? (
          <div style={{ color: "#adb5bd", fontSize: 13 }}>Loading…</div>
        ) : schemes.length === 0 ? (
          <div style={{ color: "#adb5bd", fontSize: 13 }}>
            No schemes yet — upload an Excel file above.
          </div>
        ) : (
          <>
            {/* Crop tabs */}
            <div className="d-flex gap-0"
              style={{ borderBottom: "2px solid #dee2e6" }}>
              {cropIds.map((cropId) => {
                const count    = schemes.filter((s) => s.crop_id === cropId).length;
                const isActive = activeCrop === cropId;
                return (
                  <button key={cropId} onClick={() => setActiveCrop(cropId)}
                    style={{
                      padding: "8px 20px",
                      background: "none", border: "none",
                      borderBottom: isActive ? "3px solid #185FA5" : "3px solid transparent",
                      fontSize: 13, cursor: "pointer", fontWeight: isActive ? 600 : 400,
                      color: isActive ? "#185FA5" : "#6c757d",
                      marginBottom: -2,
                    }}>
                    {cropDisplayName(cropId)}
                    <span style={{ marginLeft: 6, fontSize: 11, padding: "1px 7px",
                      borderRadius: 12,
                      background: isActive ? "#E6F1FB" : "#f1f3f5",
                      color: isActive ? "#185FA5" : "#adb5bd" }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sub-tabs */}
            <div style={{ display: "flex", gap: 4, margin: "16px 0 20px",
              borderBottom: "1px solid #dee2e6" }}>
              {[
                { key: "manage", label: "Manage and Score" },
                { key: "attach", label: "Attach Documents" },
              ].map(({ key, label }) => {
                const isActive = activeTab === key;
                return (
                  <button key={key} onClick={() => setActiveTab(key)}
                    style={{
                      padding: "6px 18px",
                      background: isActive ? "#fff" : "none",
                      border: "1px solid",
                      borderColor: isActive ? "#dee2e6" : "transparent",
                      borderBottom: isActive ? "1px solid #fff" : "1px solid transparent",
                      borderRadius: "6px 6px 0 0",
                      fontSize: 12, cursor: "pointer",
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? "#212529" : "#6c757d",
                      marginBottom: -1,
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Manage and Score tab */}
            {activeTab === "manage" && (
              CMPLX_ORDER.filter((c) => byComplexity[c]).length === 0 ? (
                <div style={{ fontSize: 13, color: "#adb5bd" }}>
                  No schemes for this crop yet.
                </div>
              ) : (
                CMPLX_ORDER.filter((c) => byComplexity[c]).map((cmplx) => (
                  <div key={cmplx} style={{ marginBottom: "1.25rem" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#6c757d",
                      marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {cmplx} complexity
                    </div>
                    <div style={{ border: "1px solid #dee2e6", borderRadius: 8, overflow: "hidden" }}>
                      <table className="table table-hover mb-0" style={{ fontSize: 12 }}>
                        <thead style={{ background: "#f8f9fa" }}>
                          <tr>
                            <th className="ps-3 py-2 fw-semibold text-muted" style={{ width: 75 }}>ID</th>
                            <th className="py-2 fw-semibold text-muted">Actor · Objective · Scheme</th>
                            <th className="py-2 fw-semibold text-muted" style={{ width: 340 }}>Expert evaluation</th>
                            <th className="pe-3 py-2" style={{ width: 80 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {byComplexity[cmplx].map((s) => (
                            <tr key={s.id}>
                              <td className="ps-3 py-2 align-middle">
                                <span style={{ fontWeight: 700, color: "#185FA5" }}>
                                  {s.id.includes("_") ? s.id.split("_").slice(1).join("_") : s.id}
                                </span>
                              </td>
                              <td className="py-2 align-middle" style={{ color: "#495057" }}>
                                {s.label?.split(" — ")[1] ?? ""}
                              </td>
                              <td className="py-2 align-middle">
                                <EvaluationCell scheme={s} onSaved={load} />
                              </td>
                              <td className="pe-3 py-2 align-middle">
                                <button className="btn btn-outline-danger btn-sm" style={{ fontSize: 11 }}
                                  onClick={() => handleDelete(s.id)}>
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )
            )}

            {/* Attach Documents tab */}
            {activeTab === "attach" && (
              <AttachDocForm
                schemesForCrop={schemesForCrop}
                activeCrop={cropDisplayName(activeCrop)}
                onAttached={load}
              />
            )}
          </>
        )}
      </Section>

    </div>
  );
}
